import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Inbox } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { Table, TableWrapper } from "@/components/ui/table";
import { Textarea } from "@/components/ui/form-controls";
import { LoadingTable, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { toast } from "@/components/ui/toast";
import { ApiFailure } from "@/components/data-display/api-failure";
import { PageHeader } from "@/components/data-display/page-header";
import { usePermission } from "@/hooks/use-permission";
import {
  describeStepUpOutcome,
  describeStepUpResumeFailure,
  useStepUpResume,
} from "@/hooks/use-step-up-resume";
import { notifyAccessRequestsChanged } from "@/hooks/use-access-request-count";
import { useStepUpLocale } from "@/i18n/use-step-up-locale";
import {
  ACCESS_REQUEST_STATUSES,
  approveAccessRequest,
  denyAccessRequest,
  fetchAccessRequest,
  fetchAccessRequests,
  fetchAdminRoles,
  idempotencyKey,
  withStepUpRetry,
  type AccessRequest,
  type AccessRequestRecommendation,
  type AccessRequestStatus,
  type AdminRoleDefinition,
  type ApiErrorInfo,
  type ResponseSupport,
} from "@/lib/api";

/**
 * Admin > Access requests (specs/access-requests S-13, UX pass; ALL-5723).
 *
 * People who signed in to this invite-only organization without an invitation. An admin
 * holding admin:users:invite approves one (choosing a role from the same live catalog Invite
 * user offers, which issues an ordinary invitation) or denies it, and either decision needs a
 * fresh second-factor step-up: the gateway answers 403 AUTH_STEP_UP_REQUIRED otherwise, and the
 * existing step-up flow runs (withStepUpRetry, resumed once on the way back by useStepUpResume).
 * The API remains the authority for every rule; this page only shows or hides the controls.
 */

const PAGE_SIZE = 25;
const REASON_MIN = 5;
const REASON_MAX = 500;

const STATUS_LABEL: Record<AccessRequestStatus, string> = {
  OPEN: "Open",
  APPROVED: "Approved",
  DENIED: "Denied",
  EXPIRED: "Expired",
};

const STATUS_TONE: Record<AccessRequestStatus, "info" | "success" | "danger" | "neutral"> = {
  OPEN: "info",
  APPROVED: "success",
  DENIED: "danger",
  EXPIRED: "neutral",
};

const EMPTY_COPY: Record<AccessRequestStatus, string> = {
  OPEN: "No one is waiting for access.",
  APPROVED: "No approved requests.",
  DENIED: "No denied requests.",
  EXPIRED: "No expired requests.",
};

const NOT_OPEN_COPY = "This request has already been decided or has expired. Nothing was changed.";

const RESUME_ACTIONS = ["admin.access_request.approve", "admin.access_request.deny"] as const;

function statusLabel(status: string): string {
  return STATUS_LABEL[status as AccessRequestStatus] ?? status;
}

function formatInstant(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleString() : "—";
}

function roleLabel(roleName: string, roles: AdminRoleDefinition[] = []): string {
  const known = roles.find((role) => role.roleName === roleName);
  if (known?.displayName) return known.displayName;
  return roleName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Who wrote a recommendation. A machine principal (an agent or API key) is always named as one,
 * so a deciding admin never mistakes an agent's triage for a colleague's. */
function recommenderLabel(rec: AccessRequestRecommendation): string {
  const who = rec.principalId || "Unknown principal";
  const type = (rec.principalType ?? "").toLowerCase();
  if (type === "human" || type === "human_user") return `${who} (person)`;
  if (type) return `${who} (agent, machine principal)`;
  return who;
}

function recommendationSummary(rec: AccessRequestRecommendation): string {
  if (rec.recommendation === "APPROVE") {
    return rec.roleName ? `Approve as ${roleLabel(rec.roleName)}` : "Approve";
  }
  if (rec.recommendation === "DENY") return "Deny";
  return rec.recommendation;
}

function latestRecommendation(request: AccessRequest): AccessRequestRecommendation | null {
  const recs = request.recommendations ?? [];
  return recs.length > 0 ? recs[recs.length - 1] : null;
}

function StatusBadge({ status }: { status: string }) {
  return <Badge variant={STATUS_TONE[status as AccessRequestStatus] ?? "neutral"}>{statusLabel(status)}</Badge>;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <span className="break-all text-right text-sm text-ink">{children}</span>
    </div>
  );
}

function reasonError(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length < REASON_MIN) return `Enter a reason of at least ${REASON_MIN} characters.`;
  if (trimmed.length > REASON_MAX) return `Keep the reason to ${REASON_MAX} characters or fewer.`;
  return null;
}

interface ListState {
  loading: boolean;
  items: AccessRequest[];
  nextCursor: string | null;
  error?: ApiErrorInfo;
}

interface DetailState {
  loading: boolean;
  item: AccessRequest | null;
  error?: ApiErrorInfo;
}

type DialogKind = "approve" | "deny";

interface DialogError extends ResponseSupport {
  title?: string;
  message: string;
}

export default function AdminAccessRequestsPage() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const canRead = usePermission("user:read");
  // The gateway's approve/deny routes require bootstrap:admin:invite, the same scope as
  // Invite user (Permission user:invite -> auth.bootstrap_admin.invite).
  const canDecide = usePermission("user:invite");
  const stepUp = useStepUpLocale();

  const [status, setStatus] = useState<AccessRequestStatus>("OPEN");
  const [list, setList] = useState<ListState>({ loading: true, items: [], nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [listToken, setListToken] = useState(0);
  const [detail, setDetail] = useState<DetailState>({ loading: false, item: null });
  const [detailToken, setDetailToken] = useState(0);

  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [roles, setRoles] = useState<AdminRoleDefinition[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<DialogError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<DialogError | null>(null);
  const [resumeFailure, setResumeFailure] = useState<DialogError | null>(null);

  const refreshAll = useCallback(() => {
    setListToken((t) => t + 1);
    setDetailToken((t) => t + 1);
    notifyAccessRequestsChanged();
  }, []);

  // A decision parked before a step-up re-authentication is completed and replayed once here,
  // on the way back to /admin/access-requests/{id} (lib/step-up-resume.ts).
  const { resuming } = useStepUpResume({
    actions: RESUME_ACTIONS,
    onResuming: () => setResumeFailure(null),
    onResumed: (record, outcome) => {
      const payload = (record.payload ?? {}) as { email?: unknown };
      const email = typeof payload.email === "string" ? payload.email : "the requester";
      toast(
        record.action === "admin.access_request.approve"
          ? { variant: "success", title: `Invitation sent to ${email}.`, supportReference: outcome.supportReference }
          : { variant: "success", title: "Request denied.", supportReference: outcome.supportReference },
      );
      refreshAll();
    },
    onFailed: (failure) => {
      const described = describeStepUpResumeFailure(failure, stepUp);
      setResumeFailure({
        title: described.title,
        message: failure.reasonCode === "ACCESS_REQUEST_NOT_OPEN" ? NOT_OPEN_COPY : described.message,
        supportReference: described.supportReference,
        capability: described.capability,
      });
      refreshAll();
    },
  });

  useEffect(() => {
    if (!canRead) return undefined;
    let cancelled = false;
    setList((prev) => ({ ...prev, loading: true, error: undefined }));
    void fetchAccessRequests({ status, limit: PAGE_SIZE }).then((result) => {
      if (cancelled) return;
      setList({ loading: false, items: result.items, nextCursor: result.nextCursor, error: result.error });
    });
    return () => {
      cancelled = true;
    };
  }, [canRead, status, listToken]);

  useEffect(() => {
    if (!canRead || !routeId) {
      setDetail({ loading: false, item: null });
      return undefined;
    }
    let cancelled = false;
    setDetail((prev) => ({ loading: true, item: prev.item?.requestId === routeId ? prev.item : null }));
    void fetchAccessRequest(routeId).then((result) => {
      if (cancelled) return;
      setDetail({ loading: false, item: result.item, error: result.error });
    });
    return () => {
      cancelled = true;
    };
  }, [canRead, routeId, detailToken]);

  async function loadMore() {
    if (!list.nextCursor) return;
    setLoadingMore(true);
    const result = await fetchAccessRequests({ status, cursor: list.nextCursor, limit: PAGE_SIZE });
    setLoadingMore(false);
    if (result.error) {
      setList((prev) => ({ ...prev, error: result.error }));
      return;
    }
    setList((prev) => ({ ...prev, items: [...prev.items, ...result.items], nextCursor: result.nextCursor }));
  }

  function openRequest(requestId: string) {
    navigate(`/admin/access-requests/${encodeURIComponent(requestId)}`);
  }

  function closeDrawer() {
    setDialog(null);
    navigate("/admin/access-requests");
  }

  function openDialog(kind: DialogKind) {
    setDialog(kind);
    setReason("");
    setReasonTouched(false);
    setDialogError(null);
    if (kind === "approve") {
      setRolesLoading(true);
      setRolesError(null);
      void fetchAdminRoles().then((result) => {
        setRolesLoading(false);
        if (result.error) {
          setRoles([]);
          setRolesError({
            message: "The role list could not be loaded, so this request cannot be approved right now.",
            supportReference: result.supportReference,
            capability: result.capability,
          });
          return;
        }
        // The same list Invite user offers: the live catalog's active roles.
        const active = result.roles.filter((role) => role.status === "ACTIVE");
        setRoles(active);
        setRoleName((current) => (current && active.some((r) => r.roleName === current) ? current : active[0]?.roleName ?? ""));
      });
    }
  }

  async function submitDecision(kind: DialogKind, request: AccessRequest) {
    setReasonTouched(true);
    if (reasonError(reason) || (kind === "approve" && !roleName)) return;
    setSubmitting(true);
    setDialogError(null);
    const trimmedReason = reason.trim();
    const key = idempotencyKey(`access-request-${kind}`);
    // One key for both attempts: the refused first call and the replay after step-up are the
    // same decision, not two.
    const outcome = await withStepUpRetry(
      () => (kind === "approve"
        ? approveAccessRequest(request.requestId, { roleName, reason: trimmedReason }, key)
        : denyAccessRequest(request.requestId, trimmedReason, key)),
      {
        action: kind === "approve" ? "admin.access_request.approve" : "admin.access_request.deny",
        payload: kind === "approve"
          ? { requestId: request.requestId, roleName, reason: trimmedReason, email: request.email }
          : { requestId: request.requestId, reason: trimmedReason, email: request.email },
        idempotencyKey: key,
        returnPath: `/admin/access-requests/${encodeURIComponent(request.requestId)}`,
      },
    );
    // The browser is on its way to the identity provider; the decision is replayed on return.
    if (outcome.redirected) return;
    setSubmitting(false);
    if (outcome.result.ok) {
      toast(
        kind === "approve"
          ? { variant: "success", title: `Invitation sent to ${request.email}.`, supportReference: outcome.result.supportReference }
          : { variant: "success", title: "Request denied.", supportReference: outcome.result.supportReference },
      );
      setDialog(null);
      setDetail((prev) => (prev.item && prev.item.requestId === request.requestId
        ? { ...prev, item: { ...prev.item, status: kind === "approve" ? "APPROVED" : "DENIED" } }
        : prev));
      refreshAll();
      return;
    }
    if (outcome.result.reasonCode === "ACCESS_REQUEST_NOT_OPEN") {
      setDialogError({
        message: NOT_OPEN_COPY,
        supportReference: outcome.result.supportReference,
        capability: outcome.result.capability,
      });
      refreshAll();
      return;
    }
    const described = describeStepUpOutcome(outcome, stepUp);
    setDialogError({ message: described.message, supportReference: described.supportReference, capability: described.capability });
  }

  if (!canRead) {
    return (
      <div className="mx-auto max-w-[1100px]">
        <PageHeader title="Access requests" />
        <PermissionDeniedState
          title="Access required"
          description="You don't have access to access requests. Ask an organization admin to grant it."
        />
      </div>
    );
  }

  const request = detail.item;
  const reasonProblem = reasonTouched ? reasonError(reason) : null;
  const columns = 7;

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader
        title="Access requests"
        subtitle="People who tried to sign in without an invitation. Approving one sends them an ordinary invitation."
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="access-request-status" className="text-xs font-semibold text-muted">Status</label>
            <Select
              id="access-request-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as AccessRequestStatus)}
            >
              {ACCESS_REQUEST_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </Select>
          </div>
        }
      />

      {resumeFailure && (
        <div role="alert" className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
          {resumeFailure.title && <p className="font-semibold">{resumeFailure.title}</p>}
          <p className="mt-0.5">{resumeFailure.message}</p>
          <SupportDetails reference={resumeFailure.supportReference} capability={resumeFailure.capability} />
        </div>
      )}
      {resuming && <p role="status" className="mb-4 text-xs text-muted">{stepUp.ui["stepUp.resuming"]}</p>}

      {list.loading ? (
        <LoadingTable rows={4} cols={columns} label="Loading access requests" />
      ) : list.error && list.items.length === 0 ? (
        <ApiFailure error={list.error} subject="access requests" onRetry={() => setListToken((t) => t + 1)} />
      ) : list.items.length === 0 ? (
        <div className="rounded-lg border border-line bg-surface px-6 py-10 text-center">
          <div className="mx-auto mb-3 grid size-10 place-items-center rounded-full bg-primary-soft">
            <Inbox aria-hidden className="size-5 text-primary" />
          </div>
          <p className="text-sm font-semibold text-ink">{EMPTY_COPY[status]}</p>
          {status === "OPEN" && (
            <Link to="/admin/invite" className="mt-2 inline-block text-sm text-primary no-underline hover:underline">
              Invite user
            </Link>
          )}
        </div>
      ) : (
        <>
          <TableWrapper>
            <Table aria-label="Access requests">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Identity provider</th>
                  <th>First tried</th>
                  <th>Last tried</th>
                  <th>Attempts</th>
                  <th>Status</th>
                  <th>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => {
                  const rec = latestRecommendation(item);
                  return (
                    <tr
                      key={item.requestId}
                      data-testid={`access-request-row-${item.requestId}`}
                      className="cursor-pointer hover:bg-bg"
                      onClick={() => openRequest(item.requestId)}
                    >
                      <td>
                        <button
                          type="button"
                          className="text-left"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRequest(item.requestId);
                          }}
                        >
                          <span className="block font-semibold text-ink">{item.displayName || item.email}</span>
                          <span className="block text-xs text-muted">{item.email}</span>
                        </button>
                      </td>
                      <td>{item.identityProviderId || "—"}</td>
                      <td>{formatInstant(item.firstSeenAt)}</td>
                      <td>{formatInstant(item.lastSeenAt)}</td>
                      <td>{item.attemptCount}</td>
                      <td><StatusBadge status={item.status} /></td>
                      <td>{rec ? recommendationSummary(rec) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
          {list.error && (
            <div role="alert" className="mt-3 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
              More requests could not be loaded.
              <SupportDetails reference={list.error.supportReference} capability={list.error.capability} />
            </div>
          )}
          {list.nextCursor && (
            <div className="mt-4 flex justify-center">
              <Button variant="secondary" onClick={() => void loadMore()} loading={loadingMore}>Load more</Button>
            </div>
          )}
        </>
      )}

      <Drawer
        open={!!routeId}
        onOpenChange={(open) => {
          if (!open) closeDrawer();
        }}
        title={request ? request.displayName || request.email : "Access request"}
        description={request ? request.email : undefined}
        width={480}
        footer={
          request && request.status === "OPEN" && canDecide ? (
            <>
              <Button variant="secondary" onClick={() => openDialog("deny")}>Deny…</Button>
              <Button onClick={() => openDialog("approve")}>Approve…</Button>
            </>
          ) : undefined
        }
      >
        {detail.loading && !request ? (
          <div role="status" className="py-4 text-sm text-muted">Loading request…</div>
        ) : detail.error && !request ? (
          <ApiFailure error={detail.error} subject="this access request" onRetry={() => setDetailToken((t) => t + 1)} />
        ) : request ? (
          <div className="space-y-5">
            <div className="rounded-[8px] border border-line bg-bg px-4">
              <DetailRow label="Name">{request.displayName || "—"}</DetailRow>
              <DetailRow label="Verified email">{request.email}</DetailRow>
              <DetailRow label="Identity provider">{request.identityProviderId || "—"}</DetailRow>
              <DetailRow label="First tried">{formatInstant(request.firstSeenAt)}</DetailRow>
              <DetailRow label="Last tried">{formatInstant(request.lastSeenAt)}</DetailRow>
              <DetailRow label="Attempts">{request.attemptCount}</DetailRow>
              <DetailRow label="Status"><StatusBadge status={request.status} /></DetailRow>
              <DetailRow label="Expires">{formatInstant(request.expiresAt)}</DetailRow>
              {request.supportReference && <DetailRow label="Support reference">{request.supportReference}</DetailRow>}
              {request.decidedAt && <DetailRow label="Decided">{formatInstant(request.decidedAt)}</DetailRow>}
              {request.decidedByPrincipalId && <DetailRow label="Decided by">{request.decidedByPrincipalId}</DetailRow>}
              {request.decisionReason && <DetailRow label="Reason">{request.decisionReason}</DetailRow>}
              {request.grantedRoleName && <DetailRow label="Role granted">{roleLabel(request.grantedRoleName, roles)}</DetailRow>}
              {request.inviteId && <DetailRow label="Invitation">{request.inviteId}</DetailRow>}
            </div>

            <section aria-labelledby="access-request-recommendations">
              <h3 id="access-request-recommendations" className="mb-2 text-sm font-bold text-ink">Recommendations</h3>
              {(request.recommendations ?? []).length === 0 ? (
                <p className="text-sm text-muted">No recommendations yet.</p>
              ) : (
                <ul className="space-y-2">
                  {(request.recommendations ?? []).map((rec, index) => (
                    <li
                      key={`${rec.principalId ?? "p"}-${rec.createdAt ?? index}`}
                      data-testid="access-request-recommendation"
                      className="rounded-[8px] border border-line px-3 py-2"
                    >
                      <p className="text-sm font-semibold text-ink">{recommendationSummary(rec)}</p>
                      {rec.rationale && <p className="mt-0.5 text-sm text-muted">{rec.rationale}</p>}
                      <p className="mt-1 text-xs text-muted">
                        By {recommenderLabel(rec)}
                        {rec.createdAt ? ` · ${formatInstant(rec.createdAt)}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>

      {request && (
        <Modal
          open={dialog === "approve"}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          title={`Approve access for ${request.displayName || request.email}`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDialog(null)} disabled={submitting}>Cancel</Button>
              <Button
                onClick={() => void submitDecision("approve", request)}
                loading={submitting}
                disabled={rolesLoading || !!rolesError || roles.length === 0 || !roleName}
              >
                Approve and send invitation
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-muted">{request.email} will get an invitation by email for the role you choose.</p>
            {rolesLoading ? (
              <div role="status" className="text-sm text-muted">Loading roles…</div>
            ) : rolesError ? (
              <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
                {rolesError.message}
                <SupportDetails reference={rolesError.supportReference} capability={rolesError.capability} />
              </div>
            ) : roles.length === 0 ? (
              <p className="text-sm text-muted">No roles are available to assign right now.</p>
            ) : (
              <div>
                <label htmlFor="access-request-role" className="mb-1.5 block text-xs font-semibold text-ink">Role</label>
                <Select
                  id="access-request-role"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  className="h-[36px] w-full"
                >
                  {roles.map((role) => (
                    <option key={role.roleName} value={role.roleName}>{roleLabel(role.roleName, roles)}</option>
                  ))}
                </Select>
              </div>
            )}
            <div>
              <label htmlFor="access-request-approve-reason" className="mb-1.5 block text-xs font-semibold text-ink">Reason</label>
              <Textarea
                id="access-request-approve-reason"
                value={reason}
                maxLength={REASON_MAX}
                rows={3}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setReasonTouched(true)}
                aria-invalid={reasonProblem ? true : undefined}
                aria-describedby={reasonProblem ? "access-request-approve-reason-error" : undefined}
              />
              {reasonProblem && (
                <p id="access-request-approve-reason-error" className="mt-1 text-xs text-danger">{reasonProblem}</p>
              )}
            </div>
            <p className="text-xs text-muted">You'll confirm with your second factor.</p>
            {dialogError && (
              <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
                {dialogError.message}
                <SupportDetails reference={dialogError.supportReference} capability={dialogError.capability} />
              </div>
            )}
          </div>
        </Modal>
      )}

      {request && (
        <Modal
          open={dialog === "deny"}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          title={`Deny access for ${request.displayName || request.email}`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDialog(null)} disabled={submitting}>Cancel</Button>
              <Button variant="destructive" onClick={() => void submitDecision("deny", request)} loading={submitting}>
                Deny request
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-muted">{request.email} is not emailed. If they sign in again, a new request opens.</p>
            <div>
              <label htmlFor="access-request-deny-reason" className="mb-1.5 block text-xs font-semibold text-ink">Reason</label>
              <Textarea
                id="access-request-deny-reason"
                value={reason}
                maxLength={REASON_MAX}
                rows={3}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setReasonTouched(true)}
                aria-invalid={reasonProblem ? true : undefined}
                aria-describedby={reasonProblem ? "access-request-deny-reason-error" : undefined}
              />
              {reasonProblem && (
                <p id="access-request-deny-reason-error" className="mt-1 text-xs text-danger">{reasonProblem}</p>
              )}
            </div>
            <p className="text-xs text-muted">You'll confirm with your second factor.</p>
            {dialogError && (
              <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
                {dialogError.message}
                <SupportDetails reference={dialogError.supportReference} capability={dialogError.capability} />
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
